import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, Trash2, RefreshCw, Search, Users, Ban, ShieldCheck, StickyNote, Download, Eraser, AlertTriangle, X, Save, Pencil, History } from "lucide-react";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Avatar } from "../components/common/Avatar";

function relativeAgo(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

function daysSince(iso) {
  if (!iso) return Infinity;
  return (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24);
}

export default function AdminPlayersPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [q, setQ] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [renameId, setRenameId] = useState(null);
  const [nameDraft, setNameDraft] = useState("");
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeDays, setPurgeDays] = useState(30);
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeBusy, setPurgeBusy] = useState(false);

  const load = async () => {
    setFetching(true);
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load players");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/"); return; }
    if (!user.is_admin) { nav("/"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const remove = async (id, email) => {
    try {
      const r = await api.delete(`/admin/users/${id}`);
      toast.success(`Removed ${email} · ${r.data.predictions_removed + r.data.market_picks_removed} picks`);
      setUsers((list) => list.filter((u) => u.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setConfirmId(null);
    }
  };

  const toggleBan = async (u) => {
    const newState = !u.is_banned;
    try {
      await api.post(`/admin/users/${u.id}/ban`, { banned: newState });
      setUsers((list) => list.map((x) => x.id === u.id ? { ...x, is_banned: newState } : x));
      toast.success(newState ? `Disabled ${u.email}` : `Re-enabled ${u.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ban action failed");
    }
  };

  const openNote = (u) => {
    setExpandedId(expandedId === u.id ? null : u.id);
    setNoteDraft(u.admin_note || "");
  };

  const saveNote = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}/note`, { note: noteDraft });
      setUsers((list) => list.map((x) => x.id === u.id ? { ...x, admin_note: noteDraft.trim() } : x));
      toast.success("Note saved");
      setExpandedId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Note save failed");
    }
  };

  const startRename = (u) => {
    setRenameId(u.id);
    setNameDraft(u.name || "");
  };

  const saveRename = async (u) => {
    const next = nameDraft.trim();
    if (!next) { toast.error("Name cannot be empty"); return; }
    if (next.length > 40) { toast.error("Name too long (40 chars max)"); return; }
    try {
      await api.patch(`/admin/users/${u.id}/name`, { name: next });
      setUsers((list) => list.map((x) => x.id === u.id ? { ...x, name: next } : x));
      toast.success(`Renamed to ${next}`);
      setRenameId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("co_token");
      const res = await fetch(`${API_BASE}/admin/users/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cricket-oracle-players-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("CSV download failed");
    }
  };

  const openPurge = async () => {
    setPurgeOpen(true);
    setPurgePreview(null);
    try {
      const r = await api.get(`/admin/users/idle-preview?days=${purgeDays}`);
      setPurgePreview(r.data);
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const refreshPurgePreview = async (d) => {
    setPurgeDays(d);
    setPurgePreview(null);
    try {
      const r = await api.get(`/admin/users/idle-preview?days=${d}`);
      setPurgePreview(r.data);
    } catch (_) {}
  };

  const confirmPurge = async () => {
    setPurgeBusy(true);
    try {
      const r = await api.post(`/admin/users/purge-idle?days=${purgeDays}`);
      toast.success(`Purged ${r.data.users_removed} players · ${r.data.predictions_removed + r.data.market_picks_removed} picks`);
      setPurgeOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally {
      setPurgeBusy(false);
    }
  };

  if (loading || !user?.is_admin) {
    return <div className="p-12 text-slate-400">Checking access…</div>;
  }

  const filtered = users.filter((u) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s);
  });

  const totalPlayers = users.length;
  const inactive14 = users.filter((u) => daysSince(u.last_activity_at) > 14 && !u.is_admin).length;
  const neverPredicted = users.filter((u) => (u.prediction_count + u.market_pick_count) === 0 && !u.is_admin).length;
  const banned = users.filter((u) => u.is_banned).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="section-label">Restricted</div>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-1 flex items-center gap-3">
            <Shield className="w-8 h-8 text-amber-400" /> Player Admin
          </h1>
          <p className="text-slate-400 text-sm mt-2">Signed-up players, activity, notes, bans and cleanup tools.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={openPurge} className="btn-ghost text-xs" data-testid="admin-purge-open">
            <Eraser className="w-3.5 h-3.5 inline mr-1" /> Purge idle…
          </button>
          <button onClick={downloadCsv} className="btn-ghost text-xs" data-testid="admin-export-csv">
            <Download className="w-3.5 h-3.5 inline mr-1" /> Export CSV
          </button>
          <button onClick={load} disabled={fetching} className="btn-ghost text-xs" data-testid="admin-refresh">
            <RefreshCw className={`w-3.5 h-3.5 inline mr-1 ${fetching ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Stat icon={<Users className="w-4 h-4 text-sky-400" />} label="Signed-up players" value={totalPlayers} />
        <Stat icon={<Users className="w-4 h-4 text-rose-400" />} label="Inactive > 14d" value={inactive14} />
        <Stat icon={<Users className="w-4 h-4 text-slate-400" />} label="Never predicted" value={neverPredicted} />
        <Stat icon={<Ban className="w-4 h-4 text-rose-500" />} label="Disabled" value={banned} />
      </div>

      <div className="tile p-0 overflow-hidden">
        <div className="p-4 border-b border-white/5 flex items-center gap-3">
          <Search className="w-4 h-4 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by name or email…"
            className="flex-1 bg-transparent outline-none text-white placeholder:text-slate-500"
            data-testid="admin-search"
          />
        </div>

        <div className="grid grid-cols-12 px-5 py-3 border-b border-white/5 section-label">
          <div className="col-span-1">Rank</div>
          <div className="col-span-3">Player</div>
          <div className="col-span-2 text-right">Signed up</div>
          <div className="col-span-2 text-right">Last active</div>
          <div className="col-span-1 text-right">Picks</div>
          <div className="col-span-1 text-right">Pts</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-8 text-slate-400 text-center" data-testid="admin-empty">No players match.</div>
        ) : filtered.map((u) => {
          const inactive = daysSince(u.last_activity_at) > 14;
          const noActivity = (u.prediction_count + u.market_pick_count) === 0;
          return (
            <div key={u.id} className={`border-b border-white/5 ${u.is_banned ? "bg-rose-500/[0.04]" : "hover:bg-white/[0.02]"}`} data-testid={`admin-row-${u.id}`}>
              <div className="grid grid-cols-12 items-center px-5 py-4">
                <div className="col-span-1">
                  {u.is_admin ? (
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center">
                      <Shield className="w-3.5 h-3.5" />
                    </div>
                  ) : (
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono-data font-bold text-sm ${
                      u.rank === 1 ? "bg-amber-500/20 border border-amber-500/40 text-amber-300" :
                      u.rank === 2 ? "bg-slate-300/15 border border-slate-300/30 text-slate-200" :
                      u.rank === 3 ? "bg-orange-600/20 border border-orange-600/40 text-orange-300" :
                      "bg-white/5 border border-white/10 text-slate-400"
                    }`} data-testid={`admin-rank-${u.id}`}>
                      {u.rank}
                    </div>
                  )}
                </div>
                <div className="col-span-3 flex items-center gap-3">
                  <Avatar userId={u.id} name={u.name} hasAvatar={u.has_avatar} version={u.avatar_version} size={36} />
                  <div className="min-w-0">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      {renameId === u.id ? (
                        <div className="inline-flex items-center gap-1" data-testid={`admin-rename-input-wrapper-${u.id}`}>
                          <input
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveRename(u); if (e.key === "Escape") setRenameId(null); }}
                            maxLength={40}
                            autoFocus
                            className="bg-[#0B0F17] border border-amber-500/40 rounded px-2 py-1 text-sm text-white outline-none focus:border-amber-400 w-40"
                            data-testid={`admin-rename-input-${u.id}`}
                          />
                          <button onClick={() => saveRename(u)} className="text-emerald-300 hover:text-emerald-200 p-1 rounded hover:bg-emerald-500/10" title="Save name" data-testid={`admin-rename-save-${u.id}`}>
                            <Save className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => setRenameId(null)} className="text-slate-400 hover:text-white p-1 rounded" title="Cancel">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <span>{u.name}</span>
                          {!u.is_admin && (
                            <button onClick={() => startRename(u)} className="text-slate-500 hover:text-amber-400 p-0.5" title="Rename player" data-testid={`admin-rename-${u.id}`}>
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                        </>
                      )}
                      {u.is_admin && <span className="pill bg-amber-500/15 text-amber-300 border border-amber-500/40 text-[9px]">Admin</span>}
                      {u.is_banned && <span className="pill bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[9px]">Disabled</span>}
                      {!u.is_admin && !u.is_banned && noActivity && <span className="pill bg-slate-800 text-slate-400 border border-slate-700 text-[9px]">Idle</span>}
                      {!u.is_admin && !u.is_banned && inactive && !noActivity && <span className="pill bg-rose-500/15 text-rose-300 border border-rose-500/40 text-[9px]">Inactive</span>}
                      {u.admin_note && <span className="pill bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 text-[9px]"><StickyNote className="w-2.5 h-2.5" />Note</span>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  </div>
                </div>
                <div className="col-span-2 text-right font-mono-data text-xs text-slate-300">{relativeAgo(u.created_at)}</div>
                <div className="col-span-2 text-right font-mono-data text-xs text-slate-300">{relativeAgo(u.last_activity_at)}</div>
                <div className="col-span-1 text-right font-mono-data text-sm">
                  <span className="text-emerald-400">{u.prediction_count}</span>
                  <span className="text-slate-600 mx-0.5">·</span>
                  <span className="text-sky-400">{u.market_pick_count}</span>
                </div>
                <div className="col-span-1 text-right metric-value text-amber-400 text-base">{u.points}</div>
                <div className="col-span-2 text-right flex justify-end items-center gap-1">
                  {u.is_admin ? (
                    <span className="text-slate-600 text-xs">—</span>
                  ) : (
                    <>
                      <button onClick={() => openNote(u)} className="text-cyan-300 hover:text-cyan-200 p-1.5 rounded hover:bg-cyan-500/10" title="Edit note" data-testid={`admin-note-${u.id}`}>
                        <StickyNote className="w-4 h-4" />
                      </button>
                      <Link to={`/admin/players/${u.id}/history`} className="text-purple-300 hover:text-purple-200 p-1.5 rounded hover:bg-purple-500/10" title="View history" data-testid={`admin-history-${u.id}`}>
                        <History className="w-4 h-4" />
                      </Link>
                      <button onClick={() => toggleBan(u)} className={`p-1.5 rounded ${u.is_banned ? "text-emerald-400 hover:bg-emerald-500/10" : "text-amber-400 hover:bg-amber-500/10"}`} title={u.is_banned ? "Re-enable" : "Disable login"} data-testid={`admin-ban-${u.id}`}>
                        {u.is_banned ? <ShieldCheck className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </button>
                      {confirmId === u.id ? (
                        <div className="inline-flex gap-1 ml-1">
                          <button onClick={() => remove(u.id, u.email)} className="px-2 py-1 rounded bg-rose-500/25 border border-rose-500 text-rose-100 text-xs font-bold" data-testid={`admin-confirm-delete-${u.id}`}>Confirm</button>
                          <button onClick={() => setConfirmId(null)} className="px-2 py-1 rounded text-slate-400 text-xs">Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmId(u.id)} className="text-rose-400 hover:text-rose-300 p-1.5 rounded hover:bg-rose-500/10" title="Remove player" data-testid={`admin-delete-${u.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {expandedId === u.id && (
                <div className="px-5 pb-4 -mt-1 fade-in">
                  <div className="tile-elev p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <StickyNote className="w-3.5 h-3.5 text-cyan-300" />
                      <div className="section-label">Private admin note · only visible to you</div>
                    </div>
                    <textarea
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      maxLength={500}
                      rows={2}
                      placeholder="e.g. verified via email · flagged suspicious login · known to admin"
                      className="w-full bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-500/50 resize-none"
                      data-testid={`admin-note-input-${u.id}`}
                    />
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-[10px] text-slate-500 font-mono-data">{noteDraft.length}/500</span>
                      <div className="flex gap-1">
                        <button onClick={() => setExpandedId(null)} className="text-slate-400 text-xs px-2 py-1">Cancel</button>
                        <button onClick={() => saveNote(u)} className="text-xs font-bold px-3 py-1.5 rounded bg-cyan-500/20 border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/30" data-testid={`admin-note-save-${u.id}`}>
                          <Save className="w-3 h-3 inline mr-1" />Save
                        </button>
                      </div>
                    </div>
                    {u.admin_note && (
                      <div className="mt-2 text-xs text-slate-400 italic border-l-2 border-cyan-500/40 pl-2">Current: "{u.admin_note}"</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-slate-500 mt-3 font-mono-data">
        Picks: <span className="text-emerald-400">over</span> · <span className="text-sky-400">markets</span>. Deletes are permanent. Ban keeps data but blocks login.
      </p>

      {purgeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md fade-in" data-testid="purge-modal">
          <div className="tile p-6 w-full max-w-lg grain relative">
            <button onClick={() => setPurgeOpen(false)} className="absolute top-3 right-3 text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <div className="font-display font-black uppercase text-xl">Bulk Purge Idle</div>
            </div>
            <p className="text-sm text-slate-400 mb-4">Delete every non-admin account with no login and no picks in the last N days. This cascades to all their predictions.</p>

            <div className="flex items-center gap-2 mb-4">
              <span className="section-label">Idle threshold:</span>
              {[14, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => refreshPurgePreview(d)}
                  className={`text-xs px-3 py-1.5 rounded-lg border ${purgeDays === d ? "bg-amber-500/20 border-amber-400 text-amber-200" : "bg-white/5 border-white/10 text-slate-300"}`}
                  data-testid={`purge-days-${d}`}
                >
                  {d}d
                </button>
              ))}
            </div>

            <div className="tile-elev p-3 max-h-64 overflow-auto mb-4">
              {!purgePreview ? (
                <div className="text-slate-400 text-sm text-center py-4">Loading preview…</div>
              ) : purgePreview.count === 0 ? (
                <div className="text-emerald-300 text-sm text-center py-4">Nothing to purge — no accounts idle beyond {purgeDays}d.</div>
              ) : (
                <div className="space-y-1">
                  <div className="section-label mb-2">Will be removed ({purgePreview.count})</div>
                  {purgePreview.users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between text-xs" data-testid={`purge-preview-row-${u.id}`}>
                      <span className="text-slate-200">{u.name}</span>
                      <span className="text-slate-500 font-mono-data">{u.email} · last seen {relativeAgo(u.last_seen_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => setPurgeOpen(false)} className="btn-ghost text-xs">Cancel</button>
              <button
                onClick={confirmPurge}
                disabled={purgeBusy || !purgePreview || purgePreview.count === 0}
                className="btn-primary text-xs disabled:opacity-40"
                data-testid="purge-confirm"
              >
                {purgeBusy ? "Purging…" : `Purge ${purgePreview?.count ?? 0} player${purgePreview?.count === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="tile p-4 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">{icon}</div>
      <div>
        <div className="section-label">{label}</div>
        <div className="metric-value text-2xl">{value}</div>
      </div>
    </div>
  );
}
